"use client";

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import LinkHandlerFrame from "@/components/waves/LinkHandlerFrame";
import { DropLocation } from "@/components/waves/drops/drop.types";
import type { DropInteractionParams } from "@/components/waves/drops/drop.types";
import WaveDropQuote from "@/components/waves/drops/WaveDropQuote";
import { WaveDropsSearchStrategy } from "@/contexts/wave/hooks/types";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { ApiWaveDropsFeed } from "@/generated/models/ApiWaveDropsFeed";
import { convertApiDropToExtendedDrop, type ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { commonApiFetch } from "@/services/api/common-api";
import QuorumParticipationDrop from "./QuorumParticipationDrop";
interface QuorumParticipationDropLinkPreviewProps {
  readonly href: string;
  readonly waveId: string;
  readonly dropId?: string | undefined;
  readonly serialNo?: string | number | undefined;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
  readonly hideLink?: boolean | undefined;
}
const toSerialNumber = (serialNo: string | number | undefined): number | null => {
  if (typeof serialNo === "number") {
    return Number.isFinite(serialNo) ? serialNo : null;
  }
  if (!serialNo) {
    return null;
  }
  const parsed = Number.parseInt(serialNo, 10);
  return Number.isFinite(parsed) ? parsed : null;
};
const fetchDropBySerialNo = async ({
  waveId,
  serialNo
}: {
  readonly waveId: string;
  readonly serialNo: number;
}): Promise<ApiDrop | null> => {
  const results = await commonApiFetch<ApiWaveDropsFeed>({
    endpoint: `waves/${waveId}/drops`,
    params: {
      limit: "1",
      serial_no_limit: `${serialNo}`,
      search_strategy: WaveDropsSearchStrategy.Both
    }
  });
  const drop = results.drops.find(candidate => candidate.serial_no === serialNo);
  if (!drop) {
    return null;
  }
  return {
    ...drop,
    wave: results.wave
  } as ApiDrop;
};
export default function QuorumParticipationDropLinkPreview(t0) {
  const $ = _c(28);
  const {
    href,
    waveId,
    dropId,
    serialNo,
    onQuoteClick,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth,
    hideLink
  } = t0;
  let t1;
  if ($[0] !== serialNo) {
    t1 = toSerialNumber(serialNo);
    $[0] = serialNo;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const parsedSerialNo = t1;
  const normalizedDropId = dropId === undefined || dropId.length === 0 ? null : dropId;
  let t2;
  let t3;
  if ($[2] !== normalizedDropId || $[3] !== parsedSerialNo || $[4] !== waveId) {
    t2 = [QueryKey.DROP, "quorum-participation-link-preview", {
      dropId: normalizedDropId,
      waveId,
      serialNo: parsedSerialNo
    }];
    t3 = async () => {
      if (normalizedDropId !== null) {
        return await commonApiFetch({
          endpoint: `drops/${normalizedDropId}`
        });
      }
      if (parsedSerialNo !== null) {
        return await fetchDropBySerialNo({
          waveId,
          serialNo: parsedSerialNo
        });
      }
      return null;
    };
    $[2] = normalizedDropId;
    $[3] = parsedSerialNo;
    $[4] = waveId;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  const t4 = normalizedDropId !== null || parsedSerialNo !== null;
  let t5;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      placeholderData: keepPreviousData
    };
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    data: drop
  } = useQuery(t5);
  let t6;
  bb0: {
    if (drop?.drop_type !== ApiDropType.Participatory) {
      t6 = null;
      break bb0;
    }
    let t7;
    if ($[11] !== drop) {
      t7 = convertApiDropToExtendedDrop(drop);
      $[11] = drop;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    t6 = t7;
  }
  const extendedDrop = t6;
  const handleReply = _temp;
  let t7;
  if ($[13] !== onQuoteClick) {
    t7 = selectedDrop => onQuoteClick(selectedDrop);
    $[13] = onQuoteClick;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleDropContentClick = t7;
  let t8;
  if ($[15] !== drop || $[16] !== embedDepth || $[17] !== embedPath || $[18] !== extendedDrop || $[19] !== handleDropContentClick || $[20] !== maxEmbedDepth || $[21] !== onQuoteClick || $[22] !== quotePath) {
    t8 = extendedDrop ? <QuorumParticipationDrop drop={extendedDrop} showWaveInfo={true} activeDrop={null} showReplyAndQuote={false} location={DropLocation.MY_STREAM} onReply={handleReply} onQuoteClick={onQuoteClick} onDropContentClick={handleDropContentClick} showInteractions={false} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} /> : <WaveDropQuote drop={drop ?? null} partId={1} onQuoteClick={onQuoteClick} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[15] = drop;
    $[16] = embedDepth;
    $[17] = embedPath;
    $[18] = extendedDrop;
    $[19] = handleDropContentClick;
    $[20] = maxEmbedDepth;
    $[21] = onQuoteClick;
    $[22] = quotePath;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== hideLink || $[25] !== href || $[26] !== t8) {
    t9 = <LinkHandlerFrame href={href} hideLink={hideLink}>{t8}</LinkHandlerFrame>;
    $[24] = hideLink;
    $[25] = href;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  return t9;
}
function _temp(_params) {}